// src/features/delegations/pages/DelegationListPage.jsx
//
// Users with delegation:create  → AdminView  (all department delegations, full filters)
// Users with delegation:read only → MyView   (own delegations as delegator or recipient)
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

import { useDelegationPermissions } from '../../auth/hooks/usePermissions';
import { useDelegationFiltering } from '../hooks/useDelegationFiltering';
import { useMyDelegations } from '../hooks/useMyDelegations';
import { DELEGATION_STATUS_COLOR, DELEGATION_STATUSES } from '../constants/delegationStatuses';
import StaffMember from '../../../components/common/StaffMember';

import PageHeader from '../../../components/common/PageHeader';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

// ── Shared column definition ────────────────────────────────────────────────

function buildColumns(t) {
  return [
    {
      field: 'title',
      headerName: t('delegations:list.grid.title'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2">{params.value}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'delegator',
      headerName: t('delegations:list.grid.delegator'),
      width: 200,
      renderCell: (params) => (
        <StaffMember staffMember={params.value} avatarSize={28} nameVariant="body2" gap={1} clickable={false} />
      ),
    },
    {
      field: 'recipient',
      headerName: t('delegations:list.grid.recipient'),
      width: 200,
      renderCell: (params) => (
        <StaffMember staffMember={params.value} avatarSize={28} nameVariant="body2" gap={1} clickable={false} />
      ),
    },
    {
      field: 'status',
      headerName: t('delegations:list.grid.status'),
      width: 140,
      renderCell: (params) => (
        <Chip
          label={t(`delegations:statuses.${params.value}`)}
          color={DELEGATION_STATUS_COLOR[params.value] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'review_date',
      headerName: t('delegations:list.grid.review_date'),
      width: 130,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2">
            {format(new Date(params.value), 'dd MMM yyyy')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        ),
    },
  ];
}

// ── Shared filter bar ───────────────────────────────────────────────────────

function FilterBar({ t, searchTerm, setSearchTerm, statuses, setStatuses, hasActiveFilters, clearFilters }) {
  return (
    <Box mb={3} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        placeholder={t('delegations:list.filter.search_placeholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ flex: 1, minWidth: 220 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="delegation-list-page-status-label">{t('delegations:list.filter.status')}</InputLabel>
        <Select labelId="delegation-list-page-status-label"
          multiple
          value={statuses}
          onChange={(e) => setStatuses(e.target.value)}
          label={t('delegations:list.filter.status')}
          renderValue={(selected) =>
            selected.map((s) => t(`delegations:statuses.${s}`)).join(', ')
          }
        >
          {DELEGATION_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              <Chip
                label={t(`delegations:statuses.${s}`)}
                color={DELEGATION_STATUS_COLOR[s]}
                size="small"
                sx={{ mr: 1 }}
              />
              <ListItemText primary={t(`delegations:statuses.${s}`)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasActiveFilters && (
        <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
          {t('delegations:list.filter.clear')}
        </Button>
      )}
    </Box>
  );
}

// ── Shared grid wrapper ─────────────────────────────────────────────────────

function DelegationsGrid({ rows, loading, error, clearError, navigate, columns }) {
  return (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.delegation_id}
        loading={loading}
        onRowClick={(params) => navigate(params.row.delegation_id)}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableColumnFilter
        disableColumnMenu
        disableColumnResize
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell': {
            minHeight: 'unset !important',
            maxHeight: 'none !important',
            height: 'auto !important',
            display: 'flex !important',
            alignItems: 'center !important',
            padding: '8px 16px !important',
          },
          minHeight: 400,
        }}
      />
    </Box>
  );
}

// ── Admin view — all department delegations ─────────────────────────────────

function AdminView() {
  const { t } = useTranslation(['delegations', 'common']);
  const navigate = useNavigate();
  const { canCreate } = useDelegationPermissions();

  const {
    delegations,
    loading,
    error,
    clearError,
    searchTerm,
    setSearchTerm,
    statuses,
    setStatuses,
    hasActiveFilters,
    clearFilters,
    refreshData,
  } = useDelegationFiltering();

  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        icon={AssignmentIndIcon}
        title={t('delegations:list.title_admin')}
        subtitle={t('delegations:list.subtitle')}
        actions={<>
          <Tooltip title={t('common:buttons.refresh')}>
            <IconButton onClick={refreshData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('create')}
            >
              {t('delegations:list.buttons.create')}
            </Button>
          )}
        </>}
      />

      <FilterBar
        t={t}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statuses={statuses}
        setStatuses={setStatuses}
        hasActiveFilters={hasActiveFilters}
        clearFilters={clearFilters}
      />

      <DelegationsGrid
        rows={delegations}
        loading={loading}
        error={error}
        clearError={clearError}
        navigate={navigate}
        columns={columns}
      />
    </Box>
  );
}

// ── My view — own delegations (delegator or recipient) ──────────────────────

function MyView() {
  const { t } = useTranslation(['delegations', 'common']);
  const navigate = useNavigate();

  const {
    delegations,
    loading,
    error,
    clearError,
    searchTerm,
    setSearchTerm,
    statuses,
    setStatuses,
    hasActiveFilters,
    clearFilters,
    refreshData,
  } = useMyDelegations();

  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {t('delegations:list.title_mine')}
        </Typography>
        <Tooltip title={t('common:buttons.refresh')}>
          <IconButton onClick={refreshData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <FilterBar
        t={t}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statuses={statuses}
        setStatuses={setStatuses}
        hasActiveFilters={hasActiveFilters}
        clearFilters={clearFilters}
      />

      <DelegationsGrid
        rows={delegations}
        loading={loading}
        error={error}
        clearError={clearError}
        navigate={navigate}
        columns={columns}
      />
    </Box>
  );
}

// ── Page entry point ────────────────────────────────────────────────────────

export default function DelegationListPage() {
  const { canCreate } = useDelegationPermissions();
  return canCreate ? <AdminView /> : <MyView />;
}
